import React, { useEffect, useState } from "react";

import { fetchStockCatalogs } from "../../services/stockCatalogs";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader } from "../ui/Card";
import { Input } from "../ui/Input";

const emptyForm = {
  name: "",
  sku: "",
  service_tag: "",
  express_service_code: "",
  category: "",
  category_id: "",
  subcategory: "",
  subcategory_id: "",
  unit: "Un",
  supplier: "",
  supplier_id: "",
  location: "",
  location_id: "",
  minimum_stock: 0,
  reorder_point: 0,
  ideal_stock: 0,
  average_cost: 0,
  lead_time_days: 0,
  description: "",
  is_active: true,
};

const EditStockItemModal = ({ isOpen, onClose, item, onSubmit }) => {
  const [form, setForm] = useState(emptyForm);
  const [catalogs, setCatalogs] = useState({
    categories: [],
    subcategories: [],
    suppliers: [],
    locations: [],
  });

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const data = await fetchStockCatalogs(true);
        setCatalogs(data);
      } catch (error) {
        console.error("Erro ao carregar cadastros de estoque:", error);
      }
    };

    if (isOpen) {
      loadCatalogs();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!item) {
      setForm(emptyForm);
      return;
    }

    setForm({
      name: item.name || "",
      sku: item.sku || "",
      service_tag: item.service_tag || "",
      express_service_code: item.express_service_code || "",
      category: item.category || "",
      category_id: item.category_id || "",
      subcategory: item.subcategory || "",
      subcategory_id: item.subcategory_id || "",
      unit: item.unit || "Un",
      supplier: item.supplier || "",
      supplier_id: item.supplier_id || "",
      location: item.location || "",
      location_id: item.location_id || "",
      minimum_stock: item.minimum_stock ?? 0,
      reorder_point: item.reorder_point ?? 0,
      ideal_stock: item.ideal_stock ?? 0,
      average_cost: item.average_cost ?? 0,
      lead_time_days: item.lead_time_days ?? 0,
      description: item.description || "",
      is_active: item.is_active !== false,
    });
  }, [item]);

  if (!isOpen || !item) return null;

  const handleChange = (key, value) => {
    setForm((prev) => {
      if (key === "category_id") {
        return { ...prev, category_id: value, subcategory_id: "" };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      alert("Informe o nome do item.");
      return;
    }

    onSubmit?.({
      ...form,
      category_id: form.category_id ? Number(form.category_id) : null,
      subcategory_id: form.subcategory_id ? Number(form.subcategory_id) : null,
      minimum_stock: Number(form.minimum_stock || 0),
      reorder_point: Number(form.reorder_point || 0),
      ideal_stock: Number(form.ideal_stock || 0),
      average_cost: Number(form.average_cost || 0),
      supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
      location_id: form.location_id ? Number(form.location_id) : null,
      lead_time_days: Number(form.lead_time_days || 0),
    });
  };

  const categoryOptions = [...catalogs.categories];
  if (item?.category && !item?.category_id && !categoryOptions.some((entry) => entry.name === item.category)) {
    categoryOptions.unshift({ id: "", name: `${item.category} (legado)` });
  }

  const supplierOptions = [...catalogs.suppliers];
  if (item?.supplier && !item?.supplier_id && !supplierOptions.some((entry) => entry.name === item.supplier)) {
    supplierOptions.unshift({ id: "", name: `${item.supplier} (legado)` });
  }

  const locationOptions = [...catalogs.locations];
  if (item?.location && !item?.location_id && !locationOptions.some((entry) => entry.name === item.location)) {
    locationOptions.unshift({ id: "", name: `${item.location} (legado)` });
  }

  const subcategoryOptions = [...catalogs.subcategories].filter((entry) => {
    if (!form.category_id) return true;
    return String(entry.category_id) === String(form.category_id);
  });
  if (
    item?.subcategory &&
    !item?.subcategory_id &&
    !subcategoryOptions.some((entry) => entry.name === item.subcategory)
  ) {
    subcategoryOptions.unshift({ id: "", name: `${item.subcategory} (legado)` });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Editar Item de Estoque</h2>
                <p className="text-sm text-gray-500">
                  Ajuste os dados cadastrais. O saldo deve ser alterado por movimentação.
                </p>
              </div>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-gray-600">Nome do Item</label>
                <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
              </div>

              <div>
                <label className="text-sm text-gray-600">SKU / Código</label>
                <Input value={form.sku} onChange={(e) => handleChange("sku", e.target.value)} />
              </div>

              <div>
                <label className="text-sm text-gray-600">Service Tag <span className="text-gray-400">(opcional)</span></label>
                <Input value={form.service_tag} onChange={(e) => handleChange("service_tag", e.target.value)} placeholder="Ex: 7XYZ123" />
              </div>

              <div>
                <label className="text-sm text-gray-600">Express Service Code <span className="text-gray-400">(opcional)</span></label>
                <Input value={form.express_service_code} onChange={(e) => handleChange("express_service_code", e.target.value)} placeholder="Ex: 12345678901" />
              </div>

              <div>
                <label className="text-sm text-gray-600">Categoria</label>
                <select
                  value={form.category_id}
                  onChange={(e) => handleChange("category_id", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Sem categoria</option>
                  {categoryOptions.map((category) => (
                    <option key={`${category.id || "legacy"}-${category.name}`} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Unidade</label>
                <select
                  value={form.unit}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="Un">Unidade (Un)</option>
                  <option value="Cx">Caixa (Cx)</option>
                  <option value="Pç">Peça (Pç)</option>
                  <option value="Pc">Pacote (Pc)</option>
                  <option value="Kg">Quilo (Kg)</option>
                  <option value="L">Litro (L)</option>
                  <option value="M">Metro (M)</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Subcategoria</label>
                <select
                  value={form.subcategory_id}
                  onChange={(e) => handleChange("subcategory_id", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Sem subcategoria</option>
                  {subcategoryOptions.map((subcategory) => (
                    <option key={`${subcategory.id || "legacy"}-${subcategory.name}`} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Fornecedor</label>
                <select
                  value={form.supplier_id}
                  onChange={(e) => handleChange("supplier_id", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Sem fornecedor</option>
                  {supplierOptions.map((supplier) => (
                    <option key={`${supplier.id || "legacy"}-${supplier.name}`} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Localização</label>
                <select
                  value={form.location_id}
                  onChange={(e) => handleChange("location_id", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Sem localização</option>
                  {locationOptions.map((location) => (
                    <option key={`${location.id || "legacy"}-${location.name}`} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Estoque Mínimo</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minimum_stock}
                  onChange={(e) => handleChange("minimum_stock", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Ponto de Pedido</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.reorder_point}
                  onChange={(e) => handleChange("reorder_point", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Estoque Ideal</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.ideal_stock}
                  onChange={(e) => handleChange("ideal_stock", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Custo Médio</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.average_cost}
                  onChange={(e) => handleChange("average_cost", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Lead Time (dias)</label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.lead_time_days}
                  onChange={(e) => handleChange("lead_time_days", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Status</label>
                <select
                  value={form.is_active ? "true" : "false"}
                  onChange={(e) => handleChange("is_active", e.target.value === "true")}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Descrição</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>Salvar</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditStockItemModal;
